import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X } from "lucide-react";

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

let deferredPrompt = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; listeners.forEach((fn) => fn()); });
  window.addEventListener("appinstalled", () => { deferredPrompt = null; listeners.forEach((fn) => fn()); });
}

export function useInstall() {
  const [, tick] = useState(0);
  useEffect(() => { const fn = () => tick((n) => n + 1); listeners.add(fn); return () => listeners.delete(fn); }, []);
  const standalone = isStandalone();
  return {
    standalone,
    ios: isIOS() && !standalone,
    canPrompt: !!deferredPrompt && !standalone,
    available: !standalone && (!!deferredPrompt || isIOS()),
    install: async () => { if (!deferredPrompt) return false; deferredPrompt.prompt(); const r = await deferredPrompt.userChoice; deferredPrompt = null; listeners.forEach((fn) => fn()); return r.outcome === "accepted"; },
  };
}

export function IOSInstallSheet({ onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50" onClick={onClose}>
      <div data-testid="ios-install-sheet" className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white p-5 pb-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div><div className="font-heading text-lg font-bold text-[#0E2240]">Install TDFMS on iPhone</div><div className="text-xs text-slate-500">Works like an app — full screen, on your home screen</div></div>
          <button onClick={onClose} className="rounded-full p-1 text-slate-400"><X size={18} /></button>
        </div>
        <ol className="mt-4 space-y-3 text-sm text-slate-700">
          <li className="flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#0E2240] text-white"><Share size={16} /></span>Open this page in <b>Safari</b> and tap the <b>Share</b> button</li>
          <li className="flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#0E2240] text-white"><PlusSquare size={16} /></span>Scroll and tap <b>Add to Home Screen</b></li>
          <li className="flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#BB162B] text-white font-bold">✓</span>Tap <b>Add</b> — TDFMS appears as an app icon</li>
        </ol>
      </div>
    </div>
  );
}

export function InstallButton({ className = "", compact = false }) {
  const inst = useInstall();
  const [ios, setIos] = useState(false);
  if (!inst.available) return null;
  const click = async () => { if (inst.canPrompt) await inst.install(); else setIos(true); };
  return (
    <>
      <button data-testid="install-app-btn" onClick={click} className={className || "flex w-full items-center justify-center gap-2 rounded-xl border border-[#0E2240]/20 bg-white px-4 py-3 text-sm font-bold text-[#0E2240]"}>
        <Download size={16} /> {compact ? "Install app" : "Install TDFMS as an app"}
      </button>
      {ios && <IOSInstallSheet onClose={() => setIos(false)} />}
    </>
  );
}
